/**
 * ProviderRole class represents the role assigned to a provider within a practice.
 * It's based on the FHIR PractitionerRole resource and provides a simple abstraction
 * to access the provider permissions through the associated RoleDefinition.
 */

import { type MedplumClient, resolveId } from "@medplum/core";
import type { Organization, Practitioner, PractitionerRole, Reference } from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ValueOf } from "@vibrant/models/fhir/fhir-resource-types";
import { createLogger } from "@/lib/logger";
import { toCodeableConcept, toFhirExtensions } from "@/lib/medplum/utils";
import { upgradeToAdminAccessPolicy } from "@/lib/migration/access-policy";
import { getSafeVibrantSuperOrgId } from "@/lib/utils";
import { Practice } from "../practice/Practice";
import { createObjectValueGuard } from "../utils";
import { Provider } from "./Provider";
import { RoleDefinition, type RoleType } from "./RoleDefinition";

const logger = createLogger("ProviderRole");

export interface ProviderRoleCreateRequest {
  practitioner: Reference<Practitioner>;
  organization: Reference<Organization>;
  role: RoleType;
  active?: boolean;
  isPracticeAdmin?: boolean;
  inviteStatus?: ProviderRoleInviteStatus;
  specialization?: string;
  supervisor?: Reference<Practitioner>;
}

export interface ProviderRoleUpdateRequest {
  role?: string;
  active?: boolean;
  isPracticeAdmin?: boolean;
  inviteStatus?: ProviderRoleInviteStatus;
  specialization?: string;
  supervisor?: Reference<Practitioner> | null;
}

export interface ProviderRoleSearchRequest {
  practitioner?: Reference<Practitioner>;
  organization?: Reference<Organization>;
  isPracticeAdmin?: boolean;
  role?: string;
  id?: string;
  includeInactive?: boolean;
}

export class ProviderRole extends FhirResource<PractitionerRole> {
  static readonly ResourceType = "PractitionerRole";

  static readonly Extension = {
    /**
     * Used to indicate the supervisor of a non-prescriber provider.
     */
    Supervisor: "vibrant.provider.supervisor",

    /**
     * Used to indicate if the provider is the owner of the practice account.
     */
    IsPracticeAdmin: "vibrant.providerRoles.isPracticeAdmin",

    /**
     * Used to indicate the invite status for the provider role.
     */
    InviteStatus: "vibrant.providerRoles.inviteStatus",

    /**
     * Provider-scoped pinned AI chat encounter ids for this practice membership.
     */
    PinnedAIChatThreadIds: "vibrant.providerRoles.ai-chat-pinned-thread-ids",
  } as const;

  static readonly Code = {
    /**
     * Used to determine the permissions of the user in a practice.
     */
    Role: "vibrant.role",
  } as const;

  static readonly InviteStatus = {
    Pending: "pending",
    Invited: "invited",
    InviteAccepted: "invite-accepted",
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                     VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------

  /**
   * Returns true if the provider role is active
   */
  get isActive(): boolean {
    return this.resource.active ?? false;
  }

  /**
   * Backwards compatibility for the isPracticeAdmin extension.
   */
  get isPracticeAdmin(): boolean {
    return this.getExtension(ProviderRole.Extension.IsPracticeAdmin)?.valueBoolean ?? false;
  }

  /**
   * Returns the role ID assigned to this provider role
   */
  get roleId(): string | undefined {
    const coding = this.resource.code?.[0]?.coding?.find(
      (c) => c.system === ProviderRole.Code.Role,
    );
    return coding?.code;
  }

  /**
   * Returns the current invite status for this provider role.
   */
  get inviteStatus(): ProviderRoleInviteStatus {
    const inviteStatus = this.getExtension(ProviderRole.Extension.InviteStatus)?.valueCode;
    if (ProviderRole.isInviteStatus(inviteStatus)) {
      return inviteStatus;
    }
    return ProviderRole.InviteStatus.Pending;
  }

  get specialty(): string | undefined {
    const coding = this.resource.specialty?.[0]?.coding?.[0];
    return coding?.code;
  }

  /**
   * Provider-scoped pinned AI chat encounter ids for this practice membership.
   */
  get pinnedAIChatThreadIds(): string[] {
    const rawValue = this.getExtension(ProviderRole.Extension.PinnedAIChatThreadIds)?.valueString;
    if (!rawValue) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return [
        ...new Set(parsedValue.filter((value): value is string => typeof value === "string")),
      ];
    } catch {
      return [];
    }
  }

  /**
   * Returns the practitioner reference
   */
  get practitioner(): Reference<Practitioner> | undefined {
    return this.resource.practitioner;
  }

  /**
   * Returns the organization reference
   */
  get organization(): Reference<Organization> | undefined {
    return this.resource.organization;
  }

  /**
   * Returns the supervisor of the non-prescriber provider.
   */
  get supervisor(): Reference<Practitioner> | undefined {
    return this.getExtension(ProviderRole.Extension.Supervisor)?.valueReference as
      | Reference<Practitioner>
      | undefined;
  }

  //------------------------------------------------------------------------------------------------
  //                                       CLASS METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Converts the provider role data to a FHIR search query format.
   *
   * @param data - The provider role data.
   * @returns The FHIR query object.
   */
  static toFhirQuery(data: ProviderRoleSearchRequest) {
    return {
      _id: data.id,
      practitioner: data.practitioner?.reference,
      organization: data.organization?.reference,
      role: data.role ? `${ProviderRole.Code.Role}|${data.role}` : undefined,
      active: data.includeInactive ? undefined : true,
    };
  }

  /**
   * Converts the provider role data to a FHIR object format.
   *
   * @param data - The provider role data.
   * @returns The FHIR object.
   */
  static toFhirObject(
    data: ProviderRoleCreateRequest | ProviderRoleUpdateRequest,
  ): ResourceData<PractitionerRole> {
    return {
      practitioner: "practitioner" in data ? data.practitioner : undefined,
      organization: "organization" in data ? data.organization : undefined,
      code: toCodeableConcept([
        {
          system: ProviderRole.Code.Role,
          code: data.role,
        },
      ]),
      active: data.active,
      specialty: toCodeableConcept([
        {
          code: data.specialization,
          display: data.specialization,
        },
      ]),
      extension: toFhirExtensions([
        {
          url: ProviderRole.Extension.IsPracticeAdmin,
          valueBoolean: data.isPracticeAdmin,
        },
        {
          url: ProviderRole.Extension.Supervisor,
          valueReference: data.supervisor,
        },
        {
          url: ProviderRole.Extension.InviteStatus,
          valueCode: data.inviteStatus,
        },
      ]),
    };
  }

  /**
   * Creates a new provider role.
   *
   * When a role is created at the Vibrant super-org, this automatically upgrades
   * the provider's AccessPolicy to the admin policy, which grants access to
   * quarantined resources.
   *
   * @param medplum - The Medplum client.
   * @param data - The provider role data.
   * @returns The created provider role.
   */
  static async create(
    medplum: MedplumClient,
    data: ProviderRoleCreateRequest,
    options?: CreateResourceOptions,
  ): Promise<ProviderRole> {
    const resource = await ProviderRole.createResource(
      medplum,
      ProviderRole.toFhirObject(data),
      options,
    );
    const providerRole = new ProviderRole(resource);

    // If this role is at the super-org, upgrade the provider to admin AccessPolicy
    const superOrgId = getSafeVibrantSuperOrgId();
    const orgId = resolveId(data.organization);

    if (superOrgId && orgId === superOrgId && data.practitioner) {
      try {
        await upgradeToAdminAccessPolicy(data.practitioner);
      } catch (err) {
        // Log but don't fail the role creation - the provider can still be
        // manually upgraded later if needed
        logger.error("Failed to upgrade provider to admin AccessPolicy", {
          practitionerRef: data.practitioner.reference,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    return providerRole;
  }

  /**
   * Searches for provider roles by practitioner and organization IDs
   *
   * @param medplum - The Medplum client
   * @param query - The query object
   * @returns The provider roles
   */
  static async search(
    medplum: MedplumClient,
    query: Partial<ProviderRoleSearchRequest>,
    options?: SearchOptions,
  ): Promise<ProviderRole[]> {
    const resources = await ProviderRole.searchResources(
      medplum,
      ProviderRole.toFhirQuery(query),
      options,
    );
    return resources.map((resource) => new ProviderRole(resource));
  }

  /**
   * Searches for a provider role by practitioner and organization IDs
   *
   * @param medplum - The Medplum client
   * @param query - The query object
   * @returns The provider role
   */
  static async searchOne(
    medplum: MedplumClient,
    query: Partial<ProviderRoleSearchRequest>,
  ): Promise<ProviderRole | undefined> {
    const resource = await ProviderRole.searchOneResource(medplum, ProviderRole.toFhirQuery(query));
    return resource ? new ProviderRole(resource) : undefined;
  }

  /**
   * Updates the role assigned to this provider
   *
   * @param medplum - The Medplum client
   * @param id - The ID of the provider role
   * @param data.role - The role ID
   * @returns The updated provider role
   */
  static async update(
    medplum: MedplumClient,
    id: string,
    data: Partial<ProviderRoleUpdateRequest>,
  ): Promise<ProviderRole> {
    const existingResource = await ProviderRole.searchOneResource(medplum, { _id: id });
    const existingRole = existingResource ? new ProviderRole(existingResource) : undefined;
    const existingInviteStatus = ProviderRole.resolveInviteStatus(existingRole);
    const existingIsPracticeAdmin = existingRole?.getExtension(
      ProviderRole.Extension.IsPracticeAdmin,
    )?.valueBoolean;
    const resolvedSupervisor =
      data.supervisor === undefined ? existingRole?.supervisor : data.supervisor;
    const resolvedInviteStatus = data.inviteStatus ?? existingInviteStatus;
    const resolvedIsPracticeAdmin = data.isPracticeAdmin ?? existingIsPracticeAdmin;

    const resource = await ProviderRole.patchResource(medplum, {
      id,
      patch: ProviderRole.toFhirObject({
        ...data,
        inviteStatus: resolvedInviteStatus,
        isPracticeAdmin: resolvedIsPracticeAdmin,
        supervisor: resolvedSupervisor,
      }),
    });

    return new ProviderRole(resource);
  }

  /**
   * Subscribes to changes in provider roles
   *
   * @param medplum - The Medplum client
   * @param query - The query object
   * @param callback - The callback function
   * @returns The subscription
   */
  static subscribe(
    medplum: MedplumClient,
    query: Partial<ProviderRoleSearchRequest>,
    callback: SubscriptionCallback<ProviderRole>,
  ) {
    return ProviderRole.subscribeToResource(medplum, ProviderRole.toFhirQuery(query), callback);
  }

  //------------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Fetches the associated practice
   */
  async getPractice(medplum: MedplumClient): Promise<Practice | undefined> {
    const practiceId = this.organization ? resolveId(this.organization) : undefined;
    return practiceId ? Practice.getById(medplum, practiceId) : undefined;
  }

  /**
   * Fetches the associated provider
   */
  async getProvider(medplum: MedplumClient): Promise<Provider | undefined> {
    const providerId = this.practitioner ? resolveId(this.practitioner) : undefined;
    return providerId ? Provider.getById(medplum, providerId) : undefined;
  }

  /**
   * Fetches the associated role definition
   */
  async getRoleDefinition(medplum: MedplumClient): Promise<RoleDefinition | undefined> {
    const roleId = this.roleId;
    return roleId ? RoleDefinition.getByRoleId(medplum, roleId) : undefined;
  }

  /**
   * Marks the invite as sent for this provider role.
   */
  async markInvited(medplum: MedplumClient): Promise<ProviderRole> {
    if (!this.id) {
      throw new Error("markInvited: provider role not found");
    }
    if (this.inviteStatus !== ProviderRole.InviteStatus.Pending) {
      return this;
    }
    return await ProviderRole.update(medplum, this.id, {
      inviteStatus: ProviderRole.InviteStatus.Invited,
    });
  }

  /**
   * Marks the invite as accepted for this provider role.
   */
  async markInviteAccepted(medplum: MedplumClient): Promise<ProviderRole> {
    if (!this.id) {
      throw new Error("markInviteAccepted: provider role not found");
    }
    if (this.inviteStatus === ProviderRole.InviteStatus.InviteAccepted) {
      return this;
    }
    return await ProviderRole.update(medplum, this.id, {
      inviteStatus: ProviderRole.InviteStatus.InviteAccepted,
    });
  }

  /**
   * Replaces the provider-scoped pinned AI chat encounter ids for this practice membership.
   */
  async setPinnedAIChatThreadIds(
    medplum: MedplumClient,
    pinnedThreadIds: string[],
  ): Promise<ProviderRole> {
    if (!this.id) {
      throw new Error("setPinnedAIChatThreadIds: provider role not found");
    }

    const uniquePinnedThreadIds = [...new Set(pinnedThreadIds.filter(Boolean))];
    const updatedResource = await ProviderRole.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: [
          {
            url: ProviderRole.Extension.PinnedAIChatThreadIds,
            valueString:
              uniquePinnedThreadIds.length > 0 ? JSON.stringify(uniquePinnedThreadIds) : null,
          },
        ],
      },
    });

    return new ProviderRole(updatedResource);
  }

  /**
   * Pins an AI chat encounter for this provider membership.
   */
  async pinAIChatThread(medplum: MedplumClient, encounterId: string): Promise<ProviderRole> {
    if (this.pinnedAIChatThreadIds.includes(encounterId)) {
      return this;
    }

    return this.setPinnedAIChatThreadIds(medplum, [...this.pinnedAIChatThreadIds, encounterId]);
  }

  /**
   * Unpins an AI chat encounter for this provider membership.
   */
  async unpinAIChatThread(medplum: MedplumClient, encounterId: string): Promise<ProviderRole> {
    if (!this.pinnedAIChatThreadIds.includes(encounterId)) {
      return this;
    }

    return this.setPinnedAIChatThreadIds(
      medplum,
      this.pinnedAIChatThreadIds.filter((pinnedThreadId) => pinnedThreadId !== encounterId),
    );
  }

  private static isInviteStatus(value: string | undefined): value is ProviderRoleInviteStatus {
    return createObjectValueGuard(ProviderRole.InviteStatus)(value);
  }

  private static resolveInviteStatus(role?: ProviderRole): ProviderRoleInviteStatus | undefined {
    if (!role) return undefined;
    const inviteStatus = role.getExtension(ProviderRole.Extension.InviteStatus)?.valueCode;
    return ProviderRole.isInviteStatus(inviteStatus) ? inviteStatus : undefined;
  }
}

export type ProviderRoleInviteStatus = ValueOf<typeof ProviderRole.InviteStatus>;
